import { FC, ReactNode, useState } from 'react'
import { TrendBadge } from 'src/components/TrendBadge'
import styles from './audienceSourceSummary.module.scss'

// Chats in the range split by audience (new / returning customer) and source
// (organic / came from an ad), each total with its % change vs the previous
// period (null = no badge). Not collected yet — the chat collector needs
// customer ids and ad referrals first (HAP-502).
export interface AudienceSourceCounts {
  changePercent: {
    new: number | null
    organic: number | null
    paid: number | null
    returning: number | null
  }
  newOrganic: number
  newPaid: number
  returningOrganic: number
  returningPaid: number
}

// Organic / Paid totals, available on pages with ad-label attribution before
// the new / returning split exists (summary.sourceCounts).
export interface SourceCounts {
  organic: { changePercent: number | null; value: number }
  paid: { changePercent: number | null; value: number }
}

interface AudienceSourceSummaryProps {
  counts?: AudienceSourceCounts | null
  sourceCounts?: SourceCounts | null
}

type SummaryTab = 'audience' | 'source'

// Until the backend sends real counts every figure is a dash under a
// "Processing" note.
const EMPTY_COUNTS: AudienceSourceCounts = {
  changePercent: { new: null, organic: null, paid: null, returning: null },
  newOrganic: 0,
  newPaid: 0,
  returningOrganic: 0,
  returningPaid: 0,
}

// Design tones: each total's colour, and the light shade its split bar ends in.
const TONES = {
  new: { dark: '#7777f6', light: '#c9c9fb' },
  organic: { dark: '#1f7a63', light: '#a9d8c6' },
  paid: { dark: '#b45309', light: '#f1c79b' },
  returning: { dark: '#68717d', light: '#cdd2d8' },
} as const

const LeafIcon = () => (
  <svg aria-hidden="true" fill="none" viewBox="0 0 16 16">
    <circle cx="8" cy="8" fill={TONES.organic.dark} r="7" />
    <path
      d="M5 11c0-3.5 2.5-6 6-6 0 3.5-2.5 6-6 6Zm0 0 3-3"
      stroke="#fff"
      strokeLinecap="round"
      strokeWidth="1.2"
    />
  </svg>
)

const DollarIcon = () => (
  <svg aria-hidden="true" fill="none" viewBox="0 0 16 16">
    <circle cx="8" cy="8" fill={TONES.paid.dark} r="7" />
    <path
      d="M9.8 5.8c-.3-.5-1-.8-1.8-.8-1 0-1.8.5-1.8 1.3 0 1.9 3.6.9 3.6 2.9 0 .8-.8 1.3-1.8 1.3-.9 0-1.6-.3-1.9-.9M8 4v8"
      stroke="#fff"
      strokeLinecap="round"
      strokeWidth="1.1"
    />
  </svg>
)

const NewIcon = () => (
  <svg aria-hidden="true" fill="none" viewBox="0 0 16 16">
    <rect fill={TONES.new.dark} height="14" rx="3" width="14" x="1" y="1" />
    <text
      fill="#fff"
      fontFamily="Roboto, sans-serif"
      fontSize="4.6"
      fontWeight="700"
      textAnchor="middle"
      x="8"
      y="9.6"
    >
      NEW
    </text>
  </svg>
)

const ReturnIcon = () => (
  <svg aria-hidden="true" fill="none" viewBox="0 0 16 16">
    <rect
      fill={TONES.returning.dark}
      height="14"
      rx="3"
      width="14"
      x="1"
      y="1"
    />
    <path
      d="M11 4.5v3.2c0 .7-.5 1.3-1.3 1.3H5.5m0 0 2-2m-2 2 2 2"
      stroke="#fff"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.3"
    />
  </svg>
)

interface CardSpec {
  change: number | null
  icon: ReactNode
  // The two parts the total splits into, dark segment first.
  parts: [{ label: string; value: number }, { label: string; value: number }]
  // Not collected yet: dashes and a neutral bar instead of figures.
  pending: boolean
  // Total known but its split isn't (Organic / Paid without new / returning).
  splitPending?: boolean
  // Total to show when the parts are unknown.
  splitTotal?: number
  title: string
  tone: { dark: string; light: string }
}

const SummaryCard: FC<CardSpec> = ({
  change,
  icon,
  parts,
  pending,
  splitPending = false,
  splitTotal,
  title,
  tone,
}) => {
  const total = splitTotal ?? parts[0].value + parts[1].value
  const partsPending = pending || splitPending
  return (
    <div className={styles.card}>
      <div className={styles.cardTitle} style={{ color: tone.dark }}>
        <span className={styles.cardIcon}>{icon}</span>
        <span className={styles.cardTitleText}>{title}</span>
      </div>
      <div className={styles.cardValueRow}>
        <span className={styles.cardValue}>
          {pending ? '–' : total.toLocaleString('en-US')}
        </span>
        {!pending && <TrendBadge value={change} />}
      </div>
      <div className={styles.splitBar}>
        {partsPending ? (
          <span className={`${styles.splitSegment} ${styles.splitEmpty}`} />
        ) : (
          parts.map((part, i) =>
            part.value > 0 || total === 0 ? (
              <span
                className={styles.splitSegment}
                key={part.label}
                style={{
                  background: i === 0 ? tone.dark : tone.light,
                  flexGrow: total === 0 ? 1 : part.value,
                }}
              />
            ) : null
          )
        )}
      </div>
      <div className={styles.splitLabels}>
        {parts.map((part) => (
          <span key={part.label}>
            <strong>
              {partsPending ? '–' : part.value.toLocaleString('en-US')}
            </strong>{' '}
            {part.label}
          </span>
        ))}
      </div>
    </div>
  )
}

export const AudienceSourceSummary: FC<AudienceSourceSummaryProps> = ({
  counts,
  sourceCounts,
}) => {
  const [activeTab, setActiveTab] = useState<SummaryTab>('source')
  const pending = !counts
  const c = counts ?? EMPTY_COUNTS
  // Organic / Paid totals without the new / returning split.
  const sourceOnly = pending && Boolean(sourceCounts)

  const cards: CardSpec[] =
    activeTab === 'audience'
      ? [
          {
            change: c.changePercent.new,
            icon: <NewIcon />,
            parts: [
              { label: 'organic', value: c.newOrganic },
              { label: 'paid', value: c.newPaid },
            ],
            pending,
            title: 'Total New',
            tone: TONES.new,
          },
          {
            change: c.changePercent.returning,
            icon: <ReturnIcon />,
            parts: [
              { label: 'organic', value: c.returningOrganic },
              { label: 'paid', value: c.returningPaid },
            ],
            pending,
            title: 'Total Returning',
            tone: TONES.returning,
          },
        ]
      : [
          {
            change: sourceOnly
              ? sourceCounts?.organic.changePercent ?? null
              : c.changePercent.organic,
            icon: <LeafIcon />,
            parts: [
              { label: 'new', value: c.newOrganic },
              { label: 'returning', value: c.returningOrganic },
            ],
            pending: pending && !sourceOnly,
            splitPending: sourceOnly,
            splitTotal: sourceOnly ? sourceCounts?.organic.value : undefined,
            title: 'Total Organic',
            tone: TONES.organic,
          },
          {
            change: sourceOnly
              ? sourceCounts?.paid.changePercent ?? null
              : c.changePercent.paid,
            icon: <DollarIcon />,
            parts: [
              { label: 'new', value: c.newPaid },
              { label: 'returning', value: c.returningPaid },
            ],
            pending: pending && !sourceOnly,
            splitPending: sourceOnly,
            splitTotal: sourceOnly ? sourceCounts?.paid.value : undefined,
            title: 'Total Paid',
            tone: TONES.paid,
          },
        ]

  return (
    <div className={styles.summary}>
      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${
            activeTab === 'audience' ? styles.activeTab : ''
          }`}
          onClick={() => setActiveTab('audience')}
          type="button"
        >
          New vs Returning
        </button>
        <button
          className={`${styles.tab} ${
            activeTab === 'source' ? styles.activeTab : ''
          }`}
          onClick={() => setActiveTab('source')}
          type="button"
        >
          Organic vs Paid
        </button>
      </div>
      <div className={styles.grid}>
        {cards.map((card) => (
          <SummaryCard key={card.title} {...card} />
        ))}
      </div>
      {pending && (
        <p className={styles.processingNote}>
          <span className={styles.processingPill}>
            <span className={styles.processingDot} />
            Processing
          </span>
          {sourceOnly && activeTab === 'source'
            ? 'New / returning splits appear once chats record new / returning customers.'
            : 'Counts appear once chats record new / returning customers and ad sources.'}
        </p>
      )}
    </div>
  )
}
